const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

const ni = (v) => {
  const n = typeof v === 'number' ? v : parseInt(v, 10);
  return Number.isFinite(n) ? n : 0;
};

export function roundMoney(v) {
  return Math.round(v * 100) / 100;
}

export function pctToFraction(p) {
  return p / 100;
}

export function normalizeFeeLayers(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((layer, idx) => {
    const mode = layer.mode === 'on_top' ? 'on_top' : 'inclusive';
    const percent = Math.max(0, Math.round(nf(layer.percent)));
    return { id: layer.id != null ? String(layer.id) : `fee-${idx}`, mode, percent };
  });
}

export function validateBudgetParts(input) {
  if (!input || typeof input !== 'object') return 'dados inválidos';
  const q = ni(input.quantity);
  if (q < 1) return 'quantidade deve ser pelo menos 1';
  const fp = ni(input.failurePercent);
  if (!Number.isInteger(fp) || fp < 0 || fp > 100) {
    return 'percentual de falha deve ser inteiro entre 0 e 100';
  }
  const layers = normalizeFeeLayers(input.feeLayers);
  for (let i = 0; i < layers.length; i++) {
    const L = layers[i];
    if (L.mode === 'inclusive' && (L.percent < 0 || L.percent >= 100)) {
      return `camada ${i + 1}: percentual inclusivo deve ser entre 0 e 99`;
    }
    if (L.mode === 'on_top' && (L.percent < 0 || L.percent >= 100)) {
      return `camada ${i + 1}: percentual adicional deve ser entre 0 e 99`;
    }
  }
  return null;
}

export function sumAmounts(entries) {
  let s = 0;
  for (const e of entries || []) {
    s += nf(typeof e === 'number' ? e : e?.amount);
  }
  return roundMoney(s);
}

export function computeDirectBlock(directLineAmounts, failurePercent) {
  const base = roundMoney(sumAmounts(directLineAmounts));
  const failureFrac = pctToFraction(Math.max(0, Math.round(nf(failurePercent))));
  const failureCost = roundMoney(base * failureFrac);
  const totalDirect = roundMoney(base + failureCost);
  return { directBase: base, failureCost, totalDirect };
}

export function applyFeeLayers(subtotalBeforeFees, feeLayers) {
  const layers = normalizeFeeLayers(feeLayers);
  let running = roundMoney(nf(subtotalBeforeFees));
  const applied = [];
  for (const layer of layers) {
    const p = pctToFraction(layer.percent);
    const before = running;
    if (layer.mode === 'inclusive') {
      running = p > 0 ? roundMoney(running / (1 - p)) : running;
    } else {
      running = roundMoney(running * (1 + p));
    }
    applied.push({
      id: layer.id,
      mode: layer.mode,
      percent: layer.percent,
      amountBefore: before,
      amountAfter: running,
    });
  }
  return { finalPrice: running, layerTrace: applied };
}

export function computeBudgetFromParts(input) {
  const err = validateBudgetParts(input);
  if (err) return { ok: false, error: err };

  const quantity = Math.max(1, ni(input.quantity));
  const directLineAmounts = input.directLineAmounts;
  const { directBase, failureCost, totalDirect } = computeDirectBlock(
    directLineAmounts,
    input.failurePercent,
  );

  const billingTotal = roundMoney(sumAmounts(input.billingLineAmounts));
  const chargedTotal = roundMoney(sumAmounts(input.chargedLineAmounts));

  const subtotalBeforeFees = roundMoney(totalDirect + billingTotal + chargedTotal);

  const { finalPrice, layerTrace } = applyFeeLayers(subtotalBeforeFees, input.feeLayers);

  const unitPrice = roundMoney(finalPrice / quantity);
  const profitOnDirectCost = roundMoney(finalPrice - totalDirect);
  let effectiveMarkupOnDirect = 0;
  if (totalDirect > 0) {
    effectiveMarkupOnDirect = roundMoney(finalPrice / totalDirect);
  }
  let marginPctOnFinal = 0;
  if (finalPrice > 0) {
    marginPctOnFinal = roundMoney((profitOnDirectCost / finalPrice) * 100);
  }

  return {
    ok: true,
    data: {
      directBase,
      failureCost,
      totalDirect,
      billingTotal,
      chargedTotal,
      subtotalBeforeFees,
      finalPrice,
      unitPrice,
      profitOnDirectCost,
      effectiveMarkupOnDirect,
      marginPctOnFinal,
      layerTrace,
      quantity,
    },
  };
}

export function buildGenericTemplateExample() {
  return {
    schema_version: 2,
    id: 'generic_hour_supply_v1',
    label: 'Serviço genérico (hora + insumo + taxas)',
    catalog: {
      supplies: [
        { id: 'insumo_a', name: 'Insumo A', unit: 'un', cost_per_unit: 12.5 },
      ],
      labor_rates: [{ id: 'std', name: 'Mão de obra padrão', rate_per_hour: 80 }],
    },
    work_schedule: { working_days_per_month: 22, working_hours_per_day: 8 },
    line_definitions: [
      {
        id: 'qty',
        role: 'quantity',
        input: 'integer',
        min: 1,
        default: 1,
      },
      {
        id: 'hours_design',
        role: 'labor_hours',
        label: 'Horas projeto',
        labor_rate_ref: 'std',
        affects: 'billing',
      },
      {
        id: 'supply_units',
        role: 'consumption',
        label: 'Unidades de insumo',
        supply_ref: 'insumo_a',
        affects: 'direct',
      },
      {
        id: 'extra_pass',
        role: 'dual_cost_charged',
        label: 'Despesa repassada',
        affects_direct: 'optional',
        affects_charged: true,
      },
    ],
    failure: { percent_field: 'failure_percent', applies_to_roles: ['direct'] },
    fee_layers: [
      { id: 'marketing', mode: 'inclusive', percent_field: 'marketing_percent' },
      { id: 'reserve', mode: 'on_top', percent_field: 'investment_percent' },
    ],
  };
}
